import { useTranslations } from 'next-intl';
import React, { useState, type ReactNode } from 'react';
import { CheckIcon } from '@mentimeter/ragnar-visuals';
import { getLocalStorageVisitorToken } from '@mentimeter/visitor/visitor-token';
import { tracking } from '@mentimeter/http-clients';
import type { UserT } from '@mentimeter/user';
import { useUser } from '@mentimeter/user';
import { showNewMessage } from '@mentimeter/intercom';
import { trackUser } from '@api/tracking/client';
import { Image } from '@mentimeter/ragnar-ui/image';
import { Button } from '@mentimeter/ragnar-ui/button';
import { Link } from '@mentimeter/ragnar-ui/link';
import { Box } from '@mentimeter/ragnar-ui/box';
import type { InviteError } from '@mentimeter/workspace';
import { Heading, P, Meta, Strong } from '@mentimeter/ragnar-ui/typography';
import type { JoinErrorEnum } from './handleErrorMessage';
import type { ClaimInvite } from './ClaimInviteToken';
import success from './images/claim-invite-token-success.png';
import { useClaimInviteLink } from './useClaimInviteLink';

const trackResendEmail = (url: string, user: UserT | null) => {
  const visitorToken = getLocalStorageVisitorToken();
  const payload = {
    event: 'Clicked resend email',
    properties: {
      context: 'join flow',
      'workspace url': url,
    },
  };

  if (user) {
    trackUser(payload);
  } else if (visitorToken) {
    tracking().trackVisitor(payload, visitorToken);
  }
};

const StrongText = (chunks: ReactNode) => <Strong>{chunks}</Strong>;
const SupportLink = (chunks: ReactNode) => (
  <Link
    underline={false}
    onClick={(ev) => {
      ev.preventDefault();
      showNewMessage();
    }}
  >
    {chunks}
  </Link>
);

export function ClaimInviteTokenSuccess({
  claimInvite,
  onClaimInviteSuccess,
  onClaimInviteError,
}: {
  claimInvite: ClaimInvite;
  onClaimInviteSuccess: () => void;
  onClaimInviteError: (error: JoinErrorEnum) => void;
}) {
  const t = useTranslations('onboarding.join_workspace');
  const { user } = useUser();
  const [disableVerify, setDisableVerify] = useState(false);
  const { email, url, claims } = claimInvite;
  const hasResentVerification = claims > 1;

  const claimInviteLink = useClaimInviteLink(url, {
    onSuccess: () => {
      onClaimInviteSuccess();
    },
    onError: (error: InviteError) => {
      onClaimInviteError(error.code);
    },
  });

  return (
    <>
      <Box height={[null, '265px']}>
        <Image src={success.src} alt={t('verification_email_sent_alt')} />
      </Box>
      <Heading textAlign="center" mt={4} mb={3} fontSize={6}>
        {t('verify_email_heading')}
      </Heading>
      <P mb={3} textAlign="center">
        {t.rich('email_sent_message', { email, Strong: StrongText })}
      </P>
      {hasResentVerification ? (
        <Button
          aria-label={t('email_resent_aria_label_a11y')}
          variant="positive"
          disabled={disableVerify}
          onClick={() => setDisableVerify(true)}
          iconLeading={CheckIcon}
        >
          {t('email_resent_button')}
        </Button>
      ) : (
        <Button
          aria-label={t('resend_email_aria_label')}
          variant="tertiary"
          onClick={async () => {
            trackResendEmail(url, user);
            claimInviteLink.trigger({ email });
          }}
        >
          {t('resend_email_button')}
        </Button>
      )}
      {disableVerify && (
        <Meta mt={3} textAlign="center">
          {t.rich('verification_issue_message', {
            SupportLink,
          })}
        </Meta>
      )}
    </>
  );
}
